import { Collapse, Spin, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';

import type { StatusUpdate } from '@/api/project-details/mocks/projectHome.types';
import Text from '@/atoms/Text';
import { HealthIndicator, type HealthColor } from '@/components/common/HealthIndicator';
import { useAuthContext } from '@/hooks/auth.store';
import type { PreviousWeeklyUpdateRecord } from '@/hooks/useWeeklyUpdate';
import { useWeeklyUpdateData } from '@/hooks/useWeeklyUpdateData';
import { DownIcon as DownOutlined } from '@/icons';
import { statusToHealthColor } from '@/utils/weeklyUpdateUtils';

const truncateText = (text: string | undefined, maxLength: number = 20): string => {
  if (!text) {
    return '';
  }
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export interface Milestone {
  title: string;
  status: string;
  dueDate: string;
}

export interface PreviousWeeklyReportData {
  date: string;
  author: string;
  healthStatus: {
    label: string;
    status: HealthColor;
    notes?: string;
  }[];
  summary: string;
  accomplishments: string[];
  planNextWeek: string[];
  risksBlockers: string[];
  milestoneSnapshot: Milestone[];
}

/**
 * Props for PreviousWeeklyReportCard support two modes:
 *
 * **Mode 1: Parent-provided data (backward compatible)**
 * Pass `data` prop directly. Component renders historical data as-is.
 *
 * **Mode 2: Independent fetching (recommended)**
 * Pass `week`, `clientId`, `projectId` to fetch data independently.
 * Fetches and transforms API data without relying on parent state.
 * Useful when parent state might be cleared or updated during editing.
 */
export interface PreviousWeeklyReportCardProps {
  data?: PreviousWeeklyReportData | PreviousWeeklyUpdateRecord;
  week?: string;
  clientId?: string;
  projectId?: string;
}

const getMilestoneStatusLabel = (status: string): string => {
  switch (status) {
    case 'on-track':
      return 'On track';
    case 'at-risk':
      return 'At risk';
    case 'completed':
      return 'Completed';
    case 'upcoming':
    default:
      return 'Upcoming';
  }
};

interface SectionProps {
  title: string;
  items: string[];
}

const Section = ({ title, items }: Readonly<SectionProps>): React.ReactElement => (
  <div className="previous-weekly-report__section">
    <h4 className="previous-weekly-report__section-title">{title}</h4>
    <div className="previous-weekly-report__section-content">
      {items.map((item) => (
        <Text key={item} variant="bodyDefault" color="var(--text-secondary)">
          {item}
        </Text>
      ))}
    </div>
  </div>
);

export function PreviousWeeklyReportCard({
  data,
  week,
  clientId,
  projectId,
}: Readonly<PreviousWeeklyReportCardProps>): React.ReactElement {
  const { accessToken } = useAuthContext();
  const [isExpanded, setIsExpanded] = useState(true);

  // Fetch data independently if week/clientId/projectId provided (independent mode)
  const { data: fetchedData, loading } = useWeeklyUpdateData(
    accessToken,
    clientId ?? '',
    projectId ?? '',
    week ?? '',
  );

  // Transform fetched data into PreviousWeeklyReportData format
  const transformedData = useMemo((): PreviousWeeklyReportData | null => {
    const sourceData = data || fetchedData;
    if (!sourceData) {
      return null;
    }

    // If data is already in PreviousWeeklyReportData format (parent-provided mode), return as-is
    if ('healthStatus' in sourceData && Array.isArray(sourceData.healthStatus)) {
      return sourceData as PreviousWeeklyReportData;
    }

    // Transform API format data (StatusUpdate or PreviousWeeklyUpdateRecord) to PreviousWeeklyReportData format
    const apiData = sourceData as StatusUpdate | PreviousWeeklyUpdateRecord;
    return {
      date: apiData.weekOf,
      author: apiData.createdBy,
      healthStatus: [
        {
          label: 'OnSch',
          status: statusToHealthColor(apiData.onSchedule?.status),
          notes: apiData.onSchedule?.notes,
        },
        {
          label: 'Budget',
          status: statusToHealthColor(
            (apiData as PreviousWeeklyUpdateRecord).onBudget?.status ?? 'pending',
          ),
          notes: (apiData as PreviousWeeklyUpdateRecord).onBudget?.notes,
        },
        {
          label: 'CSAT',
          status: statusToHealthColor(apiData.csat?.status),
          notes: apiData.csat?.notes,
        },
        {
          label: 'Team',
          status: statusToHealthColor(apiData.teamHealth?.status),
          notes: apiData.teamHealth?.notes,
        },
      ],
      summary: apiData.notes ?? 'No summary provided',
      accomplishments:
        'accomplishments' in apiData && apiData.accomplishments
          ? [apiData.accomplishments]
          : ['No accomplishments text provided'],
      planNextWeek:
        'planNextWeek' in apiData && apiData.planNextWeek
          ? [apiData.planNextWeek]
          : ['No plan text provided'],
      risksBlockers:
        'riskBlockers' in apiData && apiData.riskBlockers
          ? [apiData.riskBlockers]
          : ['No risks & blockers text provided'],
      milestoneSnapshot:
        'milestoneSnapshot' in apiData && apiData.milestoneSnapshot
          ? (apiData.milestoneSnapshot as Milestone[]).map((milestone: Milestone) => ({
              title: milestone.title,
              status: getMilestoneStatusLabel(milestone.status),
              dueDate: milestone.dueDate,
            }))
          : [],
    };
  }, [data, fetchedData]);

  // Show loading state while fetching
  if (loading && !transformedData) {
    return <Spin />;
  }

  // Show message if no data available
  if (!transformedData) {
    return (
      <div className="previous-weekly-report previous-weekly-report--empty">
        <div className="previous-weekly-report__empty-state">
          <div className="previous-weekly-report__empty-icon">📋</div>
          <Text variant="h5" className="previous-weekly-report__empty-title">
            No previous update
          </Text>
        </div>
      </div>
    );
  }

  const headerContent = (
    <div className="previous-weekly-report__header">
      <div className="previous-weekly-report__header-left">
        <DownOutlined
          rotate={isExpanded ? 0 : -90}
          className="previous-weekly-report__header-arrow"
        />
        <Text variant="h5">Previous report</Text>
        <span className="previous-weekly-report__metadata">
          <span>{transformedData.date}</span>
          <span className="previous-weekly-report__metadata-separator">·</span>
          <span>{transformedData.author}</span>
        </span>
      </div>
      <Text
        variant="caption"
        color="var(--text-secondary)"
        className="previous-weekly-report__badge"
      >
        Read-only reference
      </Text>
    </div>
  );

  return (
    <div className="previous-weekly-report">
      <Collapse
        defaultActiveKey={['1']}
        items={[
          {
            key: '1',
            label: headerContent,
            children: (
              <div className="previous-weekly-report__content">
                {/* Health Status and Summary Section - Single Line */}
                <div className="previous-weekly-report__health-summary">
                  <span className="previous-weekly-report__health-label">Health</span>
                  <div className="previous-weekly-report__health-items">
                    {transformedData.healthStatus.map((item) => (
                      <div key={item.label} className="previous-weekly-report__health-item">
                        <Text variant="bodyDefault" color="var(--text-secondary)">
                          {item.label}
                        </Text>
                        <HealthIndicator status={item.status} />
                        {item.label === 'Team' && item.notes && (
                          <Tooltip title={item.notes}>
                            <Text
                              variant="caption"
                              color="var(--text-secondary)"
                              className="previous-weekly-report__health-notes"
                              aria-label={`Team health notes: ${item.notes}`}
                            >
                              {truncateText(item.notes, 20)}
                            </Text>
                          </Tooltip>
                        )}
                      </div>
                    ))}
                  </div>
                  {transformedData.summary !== 'No summary provided' ? (
                    <Tooltip title={transformedData.summary}>
                      <Text
                        variant="bodyDefault"
                        color="var(--text-secondary)"
                        className="previous-weekly-report__summary-text previous-weekly-report__summary-text--truncated"
                        aria-label={`Summary: ${transformedData.summary}`}
                      >
                        &quot;{truncateText(transformedData.summary, 20)}&quot;
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text
                      variant="bodyDefault"
                      color="var(--text-secondary)"
                      className="previous-weekly-report__summary-text"
                    >
                      &quot;{transformedData.summary}&quot;
                    </Text>
                  )}
                </div>

                {/* Three Column Layout */}
                <div className="previous-weekly-report__layout">
                  <Section title="Accomplishments" items={transformedData.accomplishments} />
                  <Section title="Plan Next Week" items={transformedData.planNextWeek} />
                  <Section title="Risks & Blockers" items={transformedData.risksBlockers} />
                </div>

                {/* Milestone Snapshot */}
                <div className="previous-weekly-report__milestone-section">
                  <h4 className="previous-weekly-report__milestone-title">
                    Milestone Snapshot at That Time
                  </h4>
                  <div className="previous-weekly-report__milestone-grid">
                    <div className="previous-weekly-report__milestone-names">
                      {transformedData.milestoneSnapshot.map((milestone) => (
                        <Text
                          key={milestone.title}
                          variant="bodyDefault"
                          color="var(--text-secondary)"
                        >
                          {milestone.title}
                        </Text>
                      ))}
                    </div>
                    <div className="previous-weekly-report__milestone-status-dates">
                      {transformedData.milestoneSnapshot.map((milestone) => (
                        <div
                          key={`status-${milestone.title}`}
                          className="previous-weekly-report__milestone-row"
                        >
                          <span className={`status-tag status-tag--${milestone.status}`}>
                            {getMilestoneStatusLabel(milestone.status)}
                          </span>
                          <Text variant="bodyDefault" color="var(--text-secondary)">
                            {milestone.dueDate}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ),
            showArrow: false,
          },
        ]}
        onChange={(keys) => setIsExpanded(keys.length > 0)}
      />
    </div>
  );
}
